import {useEffect, useMemo, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useParams} from "react-router-dom";
import {Ban, Gift, Phone, ShieldAlert, UserRound, WalletCards} from "lucide-react";
import {WalletSignalGrid, displayValue, yesNo} from "../../shared/components/BusinessBlocks";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {DetailGrid} from "../../shared/components/DetailGrid";
import {walletAccountValue, walletLabel} from "../../shared/wallets";
import {PageToolbar, ToolbarButton, ToolbarInput, ToolbarSelect} from "../../shared/components/PageToolbar";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {asList, formatMoney} from "../../shared/utils/format";
import {
    loadUserData,
    loadUserHistory,
    loadUserListingDetail,
} from "./api";
import {mapUserApiError} from "./errors";
import {UserProfileActions} from "./UserProfileActions";
import {UserWalletCommands} from "./UserWalletCommands";
import {concreteCountryId, useUserCountryScope} from "./useUserCountryScope";
import {useTranslation} from "../../shared/i18n";


const EMPTY_FILTERS = {date_from: "", date_to: "", history_type: "", source_type: ""};

const historyAmount = (row) => {
    if (row.amount !== undefined) return formatMoney(row.amount);
    const values = [
        [walletLabel("main"), row.main_amount],
        [walletLabel("plus"), row.bonus_amount],
        [walletLabel("extra"), row.additive_amount],
        [walletLabel("frozen"), row.frozen_amount],
    ].filter(([, value]) => value !== null && value !== undefined && String(value) !== "0");
    return values.length ? values.map(([label, value]) => `${label}: ${formatMoney(value)}`).join(" · ") : "0";
};

const UserProfileWorkspace = ({userId, capabilities, countryScope}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const selectedCountryId = concreteCountryId(countryScope);
    const superadmin = capabilities.user?.role === ADMIN_ROLES.SUPERADMIN;
    // Read-only блоки и флаги страну не требуют: /users/{id}/listing, /listing/{id},
    // /status, /ref-enabled и /fraud её не принимают вовсе. Страна нужна только
    // денежным командам, которые бэкенд без неё отдаёт 403.
    const fullDetailAllowed = superadmin;
    const countryScopedActions = superadmin && selectedCountryId !== null;
    const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [historyPage, setHistoryPage] = useState(1);

    useEffect(() => {
        setDraftFilters(EMPTY_FILTERS);
        setFilters(EMPTY_FILTERS);
        setHistoryPage(1);
    }, [userId]);

    const dataKey = ["admin-user-data", countryScope, userId];
    const detailKey = ["admin-user-detail", userId];
    const historyKey = ["admin-user-history", userId, historyPage, filters];
    const identityQuery = useQuery({
        queryKey: dataKey,
        queryFn: () => loadUserData(userId, {countryId: countryScope}),
        enabled: Boolean(userId) && countryScope !== null,
    });
    const detailQuery = useQuery({
        queryKey: detailKey,
        queryFn: () => loadUserListingDetail(userId),
        enabled: Boolean(userId) && fullDetailAllowed,
    });
    const historyQuery = useQuery({
        queryKey: historyKey,
        queryFn: () => loadUserHistory(userId, {
            page: historyPage,
            ...filters,
        }),
        enabled: Boolean(userId) && fullDetailAllowed,
    });

    const user = useMemo(() => ({
        ...(detailQuery.data || {}),
        ...(identityQuery.data || {}),
        phone_number: identityQuery.data?.phone_number || detailQuery.data?.phone_number,
        ref_code: identityQuery.data?.ref_code ?? detailQuery.data?.ref_code,
    }), [detailQuery.data, identityQuery.data]);
    const account = user.account || {};
    // Ручка истории отдаёт {headers, pageCount, items} — items, а не results.
    const historyRows = asList(historyQuery.data?.items ?? historyQuery.data);
    const pageCount = Number(historyQuery.data?.pageCount || 0);
    const loading = identityQuery.isLoading || (fullDetailAllowed && detailQuery.isLoading);
    const profileError = identityQuery.error || (fullDetailAllowed ? detailQuery.error : null);

    const updateUser = (patch) => {
        const safePatch = patch && typeof patch === "object" ? patch : {};
        queryClient.setQueryData(detailKey, (current) => current ? {...current, ...safePatch} : current);
        queryClient.setQueryData(dataKey, (current) => current ? {
            ...current,
            ...(safePatch.ref_code !== undefined ? {ref_code: safePatch.ref_code} : {}),
        } : current);
        queryClient.invalidateQueries({queryKey: ["admin-users", countryScope]});
        queryClient.invalidateQueries({queryKey: ["admin-user-history", userId]});
    };

    const historyColumns = [
        {key: "created_at", label: t("sections.users.p42"), render: (row) => row.created_at || "—"},
        {key: "type", label: t("sections.users.p43"), render: (row) => row.fill_type || row.type || row.operation_type || "—"},
        {key: "bucket", label: t("sections.users.p44"), render: (row) => row.bin_type || "—"},
        {key: "source", label: t("sections.users.p45"), render: (row) => row.source_type || row.description || "—"},
        {key: "amount", label: t("sections.users.p46"), render: historyAmount},
    ];

    if (loading) {
        return <DataState title={t("sections.users.p47")} copy={t("sections.users.p48")} />;
    }
    if (profileError) {
        return (
            <div className="admin-card admin-state" role="alert">
                <strong>{t("sections.users.p49")}</strong>
                <p>{mapUserApiError(profileError).message}</p>
            </div>
        );
    }

    const canManage = capabilities.hasAction("user.manage");
    const canAdjust = capabilities.hasAction("user.balance.adjust") && superadmin;
    const title = user.username || user.phone_number || t("sections.users.p14", {v0: userId});

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("sections.users.p50")}</p>
                    <h1 className="admin-page-title">{title}</h1>
                    <p className="admin-page-copy">{t("sections.users.p51")}</p>
                </div>
            </header>

            {fullDetailAllowed ? (
                <section className="admin-grid cols-4">
                    <article className="admin-card"><p className="admin-card-title">{t("sections.users.p52")}</p><p className="admin-card-value">{formatMoney(account.current_sum)}</p></article>
                    <article className="admin-card"><p className="admin-card-title">{walletLabel("main")}</p><p className="admin-card-value">{formatMoney(walletAccountValue(account, "main"))}</p></article>
                    <article className="admin-card"><p className="admin-card-title">{walletLabel("plus")}</p><p className="admin-card-value">{formatMoney(walletAccountValue(account, "plus"))}</p></article>
                    <article className="admin-card"><p className="admin-card-title">{walletLabel("extra")}</p><p className="admin-card-value">{formatMoney(walletAccountValue(account, "extra"))}</p></article>
                </section>
            ) : null}

            <article className="admin-card">
                <p className="admin-card-title"><UserRound size={14} /> {t("sections.users.p53")}</p>
                <DetailGrid items={[
                    {label: t("sections.users.p10"), value: displayValue(user.phone_number)},
                    {label: t("sections.users.p54"), value: displayValue(user.role)},
                    {label: t("sections.users.p55"), value: displayValue(user.location?.country)},
                    {label: t("sections.users.p56"), value: displayValue(user.location?.city)},
                    {label: t("sections.users.p57"), value: displayValue(user.invited_count, 0)},
                    {label: t("sections.users.p58"), value: fullDetailAllowed ? formatMoney(user.spent_sum) : "—"},
                    {label: t("sections.users.p59"), value: fullDetailAllowed ? formatMoney(user.fill_sum) : "—"},
                ]} />
            </article>

            <article className="admin-card">
                <p className="admin-card-title"><Phone size={14} /> {t("sections.users.p60")}</p>
                <WalletSignalGrid account={account} user={user} />
            </article>

            {fullDetailAllowed ? (
                <section className="admin-grid cols-3">
                    <article className="admin-card">
                        <p className="admin-card-title"><Ban size={14} /> {t("sections.users.p61")}</p>
                        <p className="admin-card-note">{user.is_blocked ? t("sections.users.p62") : t("sections.users.p63")}</p>
                    </article>
                    <article className="admin-card">
                        <p className="admin-card-title"><Gift size={14} /> {t("sections.users.p64")}</p>
                        <p className="admin-card-note">{t("sections.users.p65")} {displayValue(user.ref_code)}{t("sections.users.p66")} {yesNo(!user.block_ref)}.</p>
                    </article>
                    <article className="admin-card">
                        <p className="admin-card-title"><ShieldAlert size={14} /> Fraud</p>
                        <p className="admin-card-note">{t("sections.users.p67")} {yesNo(user.is_fraud)}.</p>
                    </article>
                </section>
            ) : null}

            <UserProfileActions
                userId={userId}
                user={user}
                role={capabilities.user?.role}
                canManage={canManage}
                canManageFlags={fullDetailAllowed}
                countryId={countryScope}
                onUserUpdated={updateUser}
            />

            <UserWalletCommands
                userId={userId}
                user={user}
                role={capabilities.user?.role}
                canAdjust={canAdjust}
                countryScopedActions={countryScopedActions}
                countryId={countryScope}
                onUserUpdated={updateUser}
            />

            {fullDetailAllowed ? (
                <article className="admin-card">
                    <p className="admin-card-title"><WalletCards size={14} /> {t("sections.users.p68")}</p>
                    <PageToolbar>
                        <ToolbarInput label={t("sections.users.p69")} type="date" value={draftFilters.date_from} onChange={(event) => setDraftFilters((current) => ({...current, date_from: event.target.value}))} />
                        <ToolbarInput label={t("sections.users.p70")} type="date" value={draftFilters.date_to} onChange={(event) => setDraftFilters((current) => ({...current, date_to: event.target.value}))} />
                        <ToolbarSelect label={t("sections.users.p71")} value={draftFilters.history_type} onChange={(event) => setDraftFilters((current) => ({...current, history_type: event.target.value}))}>
                            <option value="">{t("sections.users.p72")}</option>
                            <option value="Пополнение">{t("sections.users.p73")}</option>
                            <option value="Покупка">{t("sections.users.p74")}</option>
                            <option value="Возврат">{t("sections.users.p75")}</option>
                        </ToolbarSelect>
                        <ToolbarSelect label={t("sections.users.p45")} value={draftFilters.source_type} onChange={(event) => setDraftFilters((current) => ({...current, source_type: event.target.value}))}>
                            <option value="">{t("sections.users.p72")}</option>
                            <option value="Покупка">{t("sections.users.p74")}</option>
                            <option value="Пополнение от заведения">{t("sections.users.p76")}</option>
                            <option value="REF от ветки">{t("sections.users.p77")}</option>
                            <option value="Пополнение POINTS">{t("sections.users.p78")}</option>
                            <option value="Пополнение PLUS">{t("sections.users.p79")}</option>
                            <option value="Welcome Bonus">Welcome Bonus</option>
                            <option value="Пополнение ЧК">{t("sections.users.p80")}</option>
                            <option value="Возврат чека">{t("sections.users.p81")}</option>
                        </ToolbarSelect>
                        <ToolbarButton onClick={() => {
                            setHistoryPage(1);
                            setFilters({...draftFilters});
                        }}>{t("sections.users.p82")}</ToolbarButton>
                        <ToolbarButton variant="secondary" onClick={() => {
                            setDraftFilters(EMPTY_FILTERS);
                            setFilters(EMPTY_FILTERS);
                            setHistoryPage(1);
                        }}>{t("sections.users.p83")}</ToolbarButton>
                    </PageToolbar>

                    {historyQuery.isError ? (
                        <div className="admin-card admin-state" role="alert">{mapUserApiError(historyQuery.error).message}</div>
                    ) : (
                        <AdminTable columns={historyColumns} rows={historyRows} loading={historyQuery.isLoading} emptyText={t("sections.users.p84")} />
                    )}
                    {pageCount > 1 ? (
                        <nav className="admin-inline-actions" aria-label={t("sections.users.p85")}>
                            <button type="button" className="admin-button admin-button-secondary" aria-label={t("sections.users.p86")} disabled={historyPage <= 1} onClick={() => setHistoryPage((current) => Math.max(1, current - 1))}>{t("sections.users.p87")}</button>
                            <span>{t("sections.users.p88")} {historyPage} {t("sections.users.p89")} {pageCount}</span>
                            <button type="button" className="admin-button admin-button-secondary" aria-label={t("sections.users.p90")} disabled={historyPage >= pageCount} onClick={() => setHistoryPage((current) => current + 1)}>{t("sections.users.p91")}</button>
                        </nav>
                    ) : null}
                </article>
            ) : null}
        </div>
    );
};

export const UserProfilePage = () => {
    const {user_id: userId} = useParams();
    const capabilities = useCapabilities();
    const {countryScope} = useUserCountryScope(capabilities);
    return (
        <UserProfileWorkspace
            key={`${userId}:${countryScope}`}
            userId={userId}
            capabilities={capabilities}
            countryScope={countryScope}
        />
    );
};
